import React, { FC, useState, useEffect } from 'react';
import { Box, Button, FormControl, Grid, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { emptyLabFormData } from './lab-form-helper';
import { LabFormData } from 'models/forms';
import AddressForm from 'components/shared/address/address-form.component';
import { Address, AddressFormDetails } from 'models/contact';
import { isValidEmail } from 'utils/validations';
import { Country } from 'models/country';
import { countryFromCode } from 'utils/country-helper';
import OrganizationNumberInput from 'components/shared/organization-number/organization-number.component';
import { Lab } from 'models/lab';
import NumberOnlyTextField from '../../number-only-text-field/number-only-text-field';
import LocaleFormatPicker from 'components/shared/locale-format-picker/locale-format-picker.component';
import { DEFAULT_LOCALE_FORMAT, LocaleFormat } from 'models/locale-format';

type LabFormProps = {
    lab: Partial<Lab>;
    onClose: () => void;
    onFormSubmitted: (formData: LabFormData) => void;
    submitButtonText: string;
};
const LabForm: FC<LabFormProps> = ({ lab, onClose, onFormSubmitted, submitButtonText }: LabFormProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'client-directory', 'organization-numbers']);
    const [currentAddressDetails, setCurrentAddressDetails] = useState<AddressFormDetails>();
    const [currentCountry, setCountry] = useState<Country>();
    const [isOrganizationNumberValid, setIsOrganizationNumberValid] = useState(true);

    const validate = (formData: LabFormData) => {
        const errors: Record<string, string> = {};
        if (!formData.name) {
            errors.name = translate('organization:VALIDATION:LAB_NAME');
        }
        if (!formData.officialName) {
            errors.officialName = translate('organization:VALIDATION:LAB_NAME');
        }
        if (!formData.organizationNumber) {
            errors.organizationNumber = translate('organization:VALIDATION:ORGANIZATION_NUMBER');
        }
        if (formData.organizationNumber && !isOrganizationNumberValid) {
            errors.organizationNumber = translate(
                `organization-numbers:${currentCountry?.alpha2Code}_INVALID_ORGANIZATION_NUMBER`,
            );
        }
        if (!formData.address || !formData.address.street) {
            errors.address = 'Address is required';
        }
        if (formData.email && !isValidEmail(formData.email)) {
            errors.email = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        return errors;
    };

    const formik = useFormik<LabFormData>({
        initialValues: emptyLabFormData(),
        validate,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: async (formData) => {
            onFormSubmitted(formData);
        },
    });

    useEffect(() => {
        formik.setValues({
            name: lab.name || '',
            officialName: lab.officialName || '',
            organizationNumber: lab.organizationNumber || '',
            crediflowPartyId: lab.crediFlow?.partyID,
            address: lab.contact?.address || ({} as Address),
            phone: lab.contact?.communication?.phone || '',
            email: lab.contact?.communication?.email || '',
            site: lab.contact?.communication?.site || '',
            localeFormat: lab.localeFormat || DEFAULT_LOCALE_FORMAT,
        } as LabFormData);
        if (lab.contact?.address) {
            setCurrentAddressDetails({
                googleId: '',
                address: lab.contact.address || ({} as Address),
                isCompleteAddress: true,
            });
        }
        if (lab.contact?.address?.country) {
            setCountry(countryFromCode(lab.contact.address.country));
        } else {
            setCountry(countryFromCode('SE'));
        }
    }, [lab]);

    const handleAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('address', address);
        if (address && address?.country !== currentCountry?.alpha2Code) {
            setCountry(countryFromCode(address.country || 'SE'));
            formik.setFieldValue('organizationNumber', '');
        }
    };

    const handleOrganizationNumberChange = (value: string, isOkay: boolean) => {
        setIsOrganizationNumberValid(isOkay);
        formik.setFieldValue('organizationNumber', value);
        setTimeout(() => {
            formik.validateForm();
        }, 0);
    };

    const onCrediflowPartyIdChange = (value: string) => {
        if (value === '') {
            formik.setFieldValue('crediflowPartyId', undefined);
        } else {
            formik.setFieldValue('crediflowPartyId', Number(value));
        }
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box mt={2} mb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            label={translate('organization:DENTHUB_LAB_NAME')}
                            required
                            fullWidth
                            helperText={formik.touched.name && formik.errors.name}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            {...formik.getFieldProps('name')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={translate('organization:ORGANIZATION_OFFICIAL_NAME')}
                            required
                            fullWidth
                            helperText={formik.touched.officialName && formik.errors.officialName}
                            error={formik.touched.officialName && Boolean(formik.errors.officialName)}
                            {...formik.getFieldProps('officialName')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {currentCountry ? (
                            <OrganizationNumberInput
                                label={translate(
                                    `organization-numbers:${currentCountry?.alpha2Code || 'SE'}_ORGANIZATION_NUMBER`,
                                )}
                                required
                                country={currentCountry}
                                fullWidth
                                onValueChange={handleOrganizationNumberChange}
                                helperText={formik.touched.organizationNumber && formik.errors.organizationNumber}
                                error={formik.touched.organizationNumber && Boolean(formik.errors.organizationNumber)}
                                onBlur={formik.getFieldProps('organizationNumber').onBlur}
                                name={formik.getFieldProps('organizationNumber').name}
                                value={formik.getFieldProps('organizationNumber').value}
                            />
                        ) : undefined}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl sx={{ maxWidth: 450 }} margin="dense" fullWidth>
                            <NumberOnlyTextField
                                id="crediFlow-partyID"
                                size="small"
                                label={translate('organization:CREDIFLOW_PARTYID')}
                                onValueChange={onCrediflowPartyIdChange}
                                onBlur={formik.getFieldProps('crediflowPartyId').onBlur}
                                name={formik.getFieldProps('crediflowPartyId').name}
                                value={formik.getFieldProps('crediflowPartyId').value}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
            <Box mb={3}>
                <AddressForm
                    onChange={handleAddressChange}
                    defaultCountryCode={currentAddressDetails?.address.country}
                    address={currentAddressDetails?.address}
                />
            </Box>
            <Box mb={4}>
                <Typography mt={1} variant="h4">
                    {translate('organization:LOCALE_FORMAT')}
                </Typography>
                <Box mt={1}>
                    <LocaleFormatPicker
                        value={formik.values.localeFormat || DEFAULT_LOCALE_FORMAT}
                        onChange={(next: LocaleFormat) => formik.setFieldValue('localeFormat', next)}
                    />
                </Box>
            </Box>
            <Box mb={4}>
                <Typography mt={1} variant="h4">
                    {translate('organization:CONTACT_INFO')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <TextField
                                id="phone"
                                margin="dense"
                                label={translate('common:PHONE')}
                                {...formik.getFieldProps('phone')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <TextField
                                id="email"
                                label={translate('common:EMAIL')}
                                margin="dense"
                                type="email"
                                helperText={formik.touched.email && formik.errors.email}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                {...formik.getFieldProps('email')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <TextField
                                id="website"
                                label={translate('common:WEBSITE')}
                                margin="dense"
                                {...formik.getFieldProps('site')}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box display="flex">
                <Box flexGrow={1}>
                    <Button onClick={() => onClose()}>{translate('common:CANCEL')}</Button>
                </Box>
                <Box>
                    <Button variant="contained" type="submit" disabled={!formik.isValid}>
                        {submitButtonText}
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default LabForm;
